// Priority SEO page: Revision plastic surgery in Vietnam.
const revision = {
  slug: "revision-plastic-surgery-vietnam",
  metaTitle: "Revision Plastic Surgery in Vietnam | Case Guidance",
  metaDescription:
    "Understand revision and corrective plastic surgery in Vietnam, including records, assessment, risks, surgeon selection, recovery and case-review requirements.",
  eyebrow: "Revision & corrective surgery",
  h1: "Revision Plastic Surgery in Vietnam",
  intro:
    "Revision surgery aims to correct or improve the result of a previous operation. These cases are often more complex than first-time procedures and usually require more detailed assessment before any plan can be proposed.",
  summary:
    "Revision cases require careful review of your history, previous records, implant information and photographs, and an in-person examination may be essential. We help organise the information needed for an appropriate professional review. Not every revision case can be accepted.",
  published: "December 2025",
  updated: "December 2025",
  breadcrumb: [{ name: "Procedures", to: "/procedures" }, { name: "Revision Surgery", to: "/revision-plastic-surgery-vietnam" }],
  sections: [
    {
      id: "what",
      h2: "What revision surgery means",
      paragraphs: [
        "Revision, or corrective, surgery addresses concerns after a previous procedure — whether that is an unsatisfactory result, a complication, or a change over time. It is different from a first-time operation and is assessed differently.",
      ],
    },
    {
      id: "complex",
      h2: "Why revision cases are more complex",
      paragraphs: [
        "Previous surgery can alter anatomy, create scar tissue and reduce the options available. A revision surgeon must work with what a previous operation has left, which can make planning and outcomes less predictable.",
      ],
      bullets: [
        "Scar tissue and altered anatomy may limit what is possible",
        "Timing between procedures can affect safety and results",
        "Realistic expectations are especially important in revision cases",
      ],
    },
    {
      id: "reasons",
      h2: "Reasons patients seek revision",
      bullets: [
        "An outcome that did not meet expectations",
        "Asymmetry or irregularities",
        "Implant-related problems",
        "Scarring concerns",
        "Functional problems, such as breathing after rhinoplasty",
        "Changes that have developed over time",
      ],
    },
    {
      id: "records",
      h2: "Previous records that may be required",
      paragraphs: ["The more information you can provide, the more useful an initial review can be."],
      subsections: [
        { h3: "Operative reports", paragraphs: ["Details of the previous operation help a surgeon understand what was done."] },
        { h3: "Implant information", paragraphs: ["Where implants were used, product and size documentation is valuable."] },
        { h3: "Photographs and imaging", paragraphs: ["Current photographs, and any imaging, support an initial assessment."] },
        { h3: "Current symptoms and concerns", paragraphs: ["A clear description of your concerns helps prioritise the review."] },
      ],
    },
    {
      id: "examination",
      h2: "Why an in-person examination may be essential",
      paragraphs: [
        "For many revision cases, photographs and records are not enough. An in-person examination may be needed to assess tissue quality, scarring and what is realistically achievable. A proposed plan may change after this examination.",
      ],
    },
    {
      id: "risks",
      h2: "Risks and realistic limitations",
      paragraphs: [
        "Revision surgery carries the general risks of surgery, and outcomes can be harder to predict than first-time procedures. A responsible surgeon will be honest about what may and may not be achievable, and results vary between patients.",
      ],
    },
    {
      id: "choosing",
      h2: "Choosing a revision surgeon and hospital",
      paragraphs: [
        "Revision work often benefits from a surgeon experienced specifically in corrective cases. Ask about their revision experience, where surgery takes place, and how complications and further revision would be handled.",
      ],
    },
    {
      id: "recovery-travel",
      h2: "Recovery and travelling for revision surgery",
      paragraphs: [
        "Recovery from revision surgery may differ from a first procedure. Plan your stay around follow-up appointments and your surgeon's travel-clearance advice, and do not book flights home until you are cleared to fly.",
      ],
    },
    {
      id: "not-accepted",
      h2: "Cases that may not be accepted",
      paragraphs: [
        "Not every revision case can be accepted. Some concerns are better managed non-surgically, some require more time before further surgery, and some fall outside what can be safely offered. An honest review is part of responsible care.",
      ],
      callout: "We cannot promise that every revision case can be accepted. An initial review explains the appropriate next steps.",
    },
    {
      id: "process",
      h2: "The revision case-review process",
      bullets: [
        "Share your concerns, history and any records through a private case review",
        "We help organise the information needed for a professional review",
        "Where appropriate, a direct consultation is arranged",
        "An in-person examination may be required before any plan is confirmed",
      ],
    },
  ],
  faqs: [
    { q: "Can you help with revision surgery in Vietnam?", a: "Yes. We help organise the information needed for an appropriate professional review of revision cases. We cannot promise that every case can be accepted." },
    { q: "What information should I provide for a revision review?", a: "Where possible: previous operative reports, implant details, current photographs and a clear description of your concerns. An in-person examination may still be required." },
    { q: "Is revision surgery riskier than first-time surgery?", a: "Revision cases are often more complex and outcomes can be harder to predict. A surgeon will discuss the specific risks and realistic limitations for your case." },
    { q: "How long should I stay for revision surgery?", a: "This depends on the procedure, follow-up and recovery. Do not book your flight home until your surgeon gives travel clearance." },
  ],
  sources: [
    { label: "International Society of Aesthetic Plastic Surgery (ISAPS)", url: "https://www.isaps.org/" },
    { label: "NHS — Cosmetic procedures: what to think about", url: "https://www.nhs.uk/conditions/cosmetic-procedures/" },
  ],
  relatedTitle: "Related revision procedures",
  related: [
    { to: "/revision-rhinoplasty-vietnam", title: "Revision rhinoplasty", desc: "Correcting previous nose surgery" },
    { to: "/breast-revision-vietnam", title: "Breast revision", desc: "Implant and result concerns" },
    { to: "/capsular-contracture-treatment-vietnam", title: "Capsular contracture treatment", desc: "Firmness around implants" },
    { to: "/breast-implant-removal-vietnam", title: "Breast implant removal", desc: "Removal or replacement" },
    { to: "/how-to-choose-a-plastic-surgeon-in-vietnam", title: "How to choose a surgeon", desc: "Especially for revision cases" },
    { to: "/plastic-surgery-safety-vietnam", title: "Is plastic surgery in Vietnam safe?", desc: "How safety is determined" },
  ],
};

export default revision;
